import { useState, useEffect, useCallback } from 'react'
import debounce from 'lodash.debounce'
import { isBrowser } from '../utils/generic'

const ORIENTATION_DEBOUNCE = 300

/**
 * Custom hook to track device orientation changes
 * @returns {string} Current orientation ('portrait' or 'landscape')
 */
export const useOrientation = () => {
  const [orientation, setOrientation] = useState(() => getOrientation())

  const updateOrientation = useCallback(() => {
    const newOrientation = getOrientation()
    setOrientation(newOrientation)
  }, [])

  useEffect(() => {
    if (!isBrowser()) return

    const debouncedUpdateOrientation = debounce(updateOrientation, ORIENTATION_DEBOUNCE)

    if (screen?.orientation) {
      screen.orientation.addEventListener(
        'change', 
        debouncedUpdateOrientation
      )
    } else if (window) {
      window.addEventListener(
        'orientationchange', 
        debouncedUpdateOrientation
      )
    }

    return () => {
      debouncedUpdateOrientation.cancel()

      if (screen?.orientation) {
        screen.orientation.removeEventListener(
          'change', 
          debouncedUpdateOrientation
        )
      } else if (window) {
        window.removeEventListener(
          'orientationchange', 
          debouncedUpdateOrientation
        )
      }
    }
  }, [updateOrientation])

  return orientation
} 

function getOrientation() {
  if (!isBrowser()) return 'portrait'
  
  if (screen?.orientation) {
    const orientation = screen.orientation.type
    return orientation.includes('portrait') ? 'portrait' : 'landscape'
  }
  
  if (window?.innerWidth && window?.innerHeight) {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  }
  
  return 'portrait'
}